import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '../redux/store';
import { settings } from '../settings';


export interface ImportarExcelResponse {
    procesoMasivoId: number;
    totalFilas: number;
    estado: string;
}

/** Estados del proceso de carga masiva. Espeja el enum del backend. */
export type EstadoProcesoMasivo =
    | 'ARCHIVO_CARGADO'
    | 'VALIDACION_EN_PROCESO'
    | 'VALIDACION_COMPLETADA'
    | 'PROCESAMIENTO_EN_PROCESO'
    | 'PROCESAMIENTO_COMPLETADO';

export type EstadoValidacionFila = 'PENDIENTE' | 'VALIDA' | 'RECHAZADA';

/** Estado del alta en SIMP de una fila (HU-2.1.5). Espeja el enum del backend. */
export type EstadoSimp = 'PENDIENTE' | 'OK' | 'ERROR';

/**
 * Estado de la sincronización con la SRT de una fila (HU-2.1.5).
 *
 * `NO_APLICA` no es un error: son las filas que se dan de alta en SIMP como
 * SRT_INCOMPLETO porque les falta un dato que la SRT exige y que no está en los
 * catálogos. Se muestra como "N/A" en gris, sin aspecto de error.
 */
export type EstadoSrt = 'PENDIENTE' | 'OK' | 'ERROR' | 'NO_APLICA';

/** Filtro de la tabla de resultados. `all` no filtra. */
export type FiltroEstadoValidacion = EstadoValidacionFila | 'all';
export type FiltroEstadoSimp = EstadoSimp | 'all';
export type FiltroEstadoSrt = EstadoSrt | 'all';

export interface ValidarProcesoResponse {
    procesoMasivoId: number;
    estado: EstadoProcesoMasivo;
    totalFilas: number;
}

export interface ProgresoValidacionResponse {
    /**
     * Cabecera del proceso (HU-2.1.7).
     *
     * La necesita el reingreso por URL: sin el nombre del archivo y el total de
     * filas, el paso 1 de un proceso retomado mostraría el selector de archivo en
     * vez del archivo ya cargado.
     */
    procesoMasivoId: number;
    archivoNombre: string | null;
    fechaAlta: string;
    estado: EstadoProcesoMasivo;
    resumen: {
        totalFilas: number;
        totalValidados: number;
        totalFallidos: number;
        totalPendientes: number;
        porcentaje: number;
    };
    erroresAgrupados: Record<string, number>;
    puedeProcesar: boolean;
    /**
     * Progreso del paso 3 (HU-2.1.5).
     *
     * `totalAProcesar` son las filas VÁLIDAS, no las del archivo: el denominador
     * del "X de N" del paso 3 es distinto al del paso 2.
     */
    procesamiento: {
        totalAProcesar: number;
        totalProcesadas: number;
        totalPendientes: number;
        porcentaje: number;
        simpOk: number;
        simpError: number;
        srtOk: number;
        srtError: number;
        srtNoAplica: number;
        /**
         * Fallidas que todavía pueden reintentarse, y las que ya no (BUG-008).
         *
         * `simpError` y `srtError` NO sirven para habilitar "Reprocesar fallidos":
         * también cuentan las filas que agotaron sus 3 intentos, que el reproceso
         * va a ignorar. Con ese cálculo el botón quedaba habilitado, el backend
         * rechazaba el reproceso y el usuario se enteraba después del click.
         */
        reintentables: number;
        agotadas: number;
    };
}

export interface ProcesarProcesoResponse {
    procesoMasivoId: number;
    /** Cantidad de filas encoladas. Es el N del "X de N" del paso 3. */
    registrosAProcesar: number;
}

/** Respuesta de POST /procesos-masivos/{id}/reprocesar-fallidos (HU-2.1.6). */
export interface ReprocesarFallidosResponse {
    procesoMasivoId: number;
    registrosAReprocesar: {
        /** Filas que retoman desde el alta en SIMP. */
        simpError: number;
        /** Filas que retoman desde la SRT, sobre el establecimiento ya creado. */
        srtError: number;
        total: number;
    };
    /**
     * Fallidas que quedaron afuera por haber agotado sus intentos.
     *
     * Es lo que le permite a la pantalla distinguir "no hay nada que reprocesar"
     * de "ya no se puede reintentar", que para el usuario son dos situaciones
     * muy distintas.
     */
    sinReintentosDisponibles: number;
}

export interface DetalleValidacionFila {
    numeroFila: number;
    nombreEstablecimiento: string | null;
    direccion: string | null;
    codigoCiiu: string | null;
    estadoValidacion: EstadoValidacionFila;
    errorValidacion: string | null;
    /** Texto legible del rechazo; lo resuelve el backend. */
    motivo: string | null;
    /** Una fila válida también puede traer advertencias (ej: sin geolocalización). */
    advertencias: string[];
    /**
     * Estado y error de cada etapa del procesamiento (HU-2.1.5).
     *
     * Los mensajes vienen literales del backend —los produce la base o la SRT— y
     * se muestran tal cual: casi siempre nombran el campo que falla, que es lo
     * único accionable para el usuario.
     */
    estadoSimp: EstadoSimp;
    errorSimp: string | null;
    estadoSrt: EstadoSrt;
    errorSrt: string | null;
    /**
     * La fila agotó los intentos de la etapa que le falló: el reproceso ya no la
     * va a tomar (BUG-008).
     *
     * Lo resuelve el backend y llega como booleano: la regla es la misma que
     * alimenta el conteo de `reintentables` en /progreso, y replicarla acá
     * habilitaría que la tabla y el botón se contradigan.
     */
    sinReintentos: boolean;
}

export interface DetallesValidacionResponse {
    detalles: DetalleValidacionFila[];
    paginacion: {
        pagina: number;
        total: number;
        totalPaginas: number;
        limite: number;
    };
}

/** Una carga tal como la muestra una fila de la grilla (HU-2.1.7). */
export interface ProcesoMasivoListadoItem {
    procesoMasivoId: number;
    /** ISO: el backend serializa la fecha como string en el JSON. */
    fechaAlta: string;
    archivoNombre: string | null;
    estado: EstadoProcesoMasivo;
    totalFilas: number;
    totalValidados: number;
    totalFallidos: number;
    /** Totales del procesamiento. Hoy siempre 0: los llena HU-2.1.5. */
    totalSimpOk: number;
    totalSimpError: number;
    totalSrtOk: number;
    totalSrtError: number;
}

export interface ProcesosMasivosResponse {
    procesos: ProcesoMasivoListadoItem[];
    /**
     * El proceso en curso del usuario, o `null`.
     *
     * Lo resuelve el backend sin paginar: el activo puede quedar fuera de la
     * página listada, así que derivarlo de `procesos` daría un falso negativo.
     */
    procesoActivo: { procesoMasivoId: number; estado: EstadoProcesoMasivo } | null;
    paginacion: {
        pagina: number;
        total: number;
        totalPaginas: number;
        limite: number;
    };
}

export interface DetallesValidacionArgs {
    procesoMasivoId: number;
    estadoValidacion: FiltroEstadoValidacion;
    /** Filtros del paso 3 (HU-2.1.5). Combinables entre sí y con el de validación. */
    estadoSimp?: FiltroEstadoSimp;
    estadoSrt?: FiltroEstadoSrt;
    pagina: number;
    limite: number;
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

export const cargaMasivaApi = createApi({
    reducerPath: 'cargaMasivaApi',
    baseQuery: baseQuery,
    endpoints: (builder) => ({
        /**
         * Las dos descargas piden el binario directo (BUG-004).
         *
         * Antes pedían `response.json()` y reconstruían el Blob desde un
         * { data: number[] }, porque el backend devolvía el Buffer sin decorar y
         * NestJS lo serializaba a JSON. El comentario de ese workaround culpaba a
         * API Gateway; la causa real era que faltaba `binarySettings` en el
         * arranque del Lambda.
         *
         * ⚠️ Esto NO funciona contra un backend que no tenga ese arreglo: sobre una
         * respuesta JSON, `blob()` devuelve un Blob con el texto del JSON adentro y
         * se descarga un .xlsx ilegible. Los dos PRs se despliegan juntos, backend
         * primero.
         */
        downloadTemplateExcel: builder.query<Blob, void>({
            query: () => ({
                url: '/establecimientos/template-excel',
                method: 'GET',
                responseHandler: (response) => response.blob()
            })
        }),
        downloadCatalogosExcel: builder.query<Blob, void>({
            query: () => ({
                url: '/establecimientos/catalogos-excel',
                method: 'GET',
                responseHandler: (response) => response.blob()
            })
        }),
        /**
         * Sube el Excel de carga masiva (HU-2.1.3).
         *
         * No se setea Content-Type a mano: el navegador tiene que generar el
         * boundary del multipart.
         */
        importarExcel: builder.mutation<
            ImportarExcelResponse,
            { file: File; idOrganismoFiltro?: number }
        >({
            query: ({ file, idOrganismoFiltro }) => {
                const formData = new FormData();
                formData.append('file', file);
                if (idOrganismoFiltro !== undefined) {
                    formData.append('idOrganismoFiltro', String(idOrganismoFiltro));
                }
                return {
                    url: '/establecimientos/importar-excel',
                    method: 'POST',
                    body: formData
                };
            }
        }),
        /**
         * Historial de cargas masivas del usuario (HU-2.1.7).
         *
         * Es el punto de entrada del módulo: alimenta la grilla y trae el proceso
         * activo, que es lo que decide si "Nueva carga" puede navegar.
         */
        getProcesosMasivos: builder.query<
            ProcesosMasivosResponse,
            { pagina: number; limite: number }
        >({
            query: ({ pagina, limite }) => ({
                url: '/establecimientos/procesos-masivos',
                method: 'GET',
                // El backend corre con forbidNonWhitelisted: cualquier param extra
                // acá devuelve 400.
                params: { pagina, limite }
            })
        }),
        /**
         * Dispara la validación de las filas (HU-2.1.4).
         *
         * Responde 202: la validación corre en background y el avance se sigue con
         * getProgresoValidacion.
         */
        validarProceso: builder.mutation<ValidarProcesoResponse, number>({
            query: (procesoMasivoId) => ({
                url: `/establecimientos/procesos-masivos/${procesoMasivoId}/validar`,
                method: 'POST'
            })
        }),
        getProgresoValidacion: builder.query<ProgresoValidacionResponse, number>({
            query: (procesoMasivoId) => ({
                url: `/establecimientos/procesos-masivos/${procesoMasivoId}/progreso`,
                method: 'GET'
            })
        }),
        getDetallesValidacion: builder.query<
            DetallesValidacionResponse,
            DetallesValidacionArgs
        >({
            query: ({
                procesoMasivoId,
                estadoValidacion,
                estadoSimp,
                estadoSrt,
                pagina,
                limite
            }) => ({
                url: `/establecimientos/procesos-masivos/${procesoMasivoId}/detalles`,
                method: 'GET',
                // El backend corre con forbidNonWhitelisted: cualquier param extra
                // acá devuelve 400.
                params: {
                    estado_validacion: estadoValidacion,
                    ...(estadoSimp ? { estado_simp: estadoSimp } : {}),
                    ...(estadoSrt ? { estado_srt: estadoSrt } : {}),
                    pagina,
                    limite
                }
            })
        }),
        /**
         * Dispara el procesamiento de las filas válidas (HU-2.1.5).
         *
         * Responde 202: el alta en SIMP y el envío a la SRT corren en background,
         * de a una fila y en orden. El avance se sigue con getProgresoValidacion.
         *
         * ⚠️ Es el único paso irreversible del módulo. La confirmación previa la
         * pide ConfirmarProcesamientoDialog.
         */
        procesarProceso: builder.mutation<ProcesarProcesoResponse, number>({
            query: (procesoMasivoId) => ({
                url: `/establecimientos/procesos-masivos/${procesoMasivoId}/procesar`,
                method: 'POST'
            })
        }),

        /**
         * Reencola las filas que fallaron, cada una desde la etapa que falló
         * (HU-2.1.6).
         *
         * Responde 202 y devuelve el proceso a PROCESAMIENTO_EN_PROCESO, así que
         * el polling de getProgresoValidacion retoma solo y la pantalla vuelve a
         * mostrar el avance en vivo sin tocar nada.
         *
         * 409 cuando no hay nada que reprocesar, y el mensaje distingue si fue
         * porque no hay fallidos o porque agotaron sus intentos.
         */
        reprocesarFallidos: builder.mutation<ReprocesarFallidosResponse, number>({
            query: (procesoMasivoId) => ({
                url: `/establecimientos/procesos-masivos/${procesoMasivoId}/reprocesar-fallidos`,
                method: 'POST'
            })
        })
    })
});

export const {
    useLazyDownloadTemplateExcelQuery,
    useLazyDownloadCatalogosExcelQuery,
    useImportarExcelMutation,
    useGetProcesosMasivosQuery,
    useValidarProcesoMutation,
    useProcesarProcesoMutation,
    useReprocesarFallidosMutation,
    useGetProgresoValidacionQuery,
    useGetDetallesValidacionQuery
} = cargaMasivaApi;
